import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const WS_BASE_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') || 'ws://localhost:8001';

export function useTaskWebSocket(taskId, options = {}) {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user')) || null;
    const [isConnected, setIsConnected] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [activeUsers, setActiveUsers] = useState([]);
    const [typingUsers, setTypingUsers] = useState({});
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const pingIntervalRef = useRef(null);
    
    const { onFieldUpdate, onUserJoined, onUserLeft, onCommentAdded, onCommentUpdated, onCommentDeleted, autoReconnect = true, enableWebSocket = false } = options;

    const connect = useCallback(() => {
        if (!taskId || !token || !user || !enableWebSocket) return;
        
        // Close existing connection
        if (wsRef.current) {
            wsRef.current.close();
        }
        
        const wsUrl = `${WS_BASE_URL}/api/ws/task/${taskId}/${user.id}?token=${token}`;
        console.log('Connecting to WebSocket:', wsUrl);
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        
        ws.onopen = () => {
            console.log('WebSocket connected');
            setIsConnected(true);
            // Start ping interval to keep connection alive
            pingIntervalRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: 'ping' }));
                }
            }, 30000); // Ping every 30 seconds
        };
        
        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('WebSocket message:', data);
                
                switch (data.type) {
                    case 'field_updated':
                        setLastUpdate({
                            field: data.field,
                            value: data.value,
                            updatedBy: data.updated_by_name,
                            timestamp: data.timestamp
                        });
                        if (onFieldUpdate) {
                            onFieldUpdate(data.field, data.value, data.updated_by);
                        }
                        break;
                    
                    case 'update_confirmed':
                        // Update confirmed - could show a subtle indicator
                        setLastUpdate({
                            field: data.field,
                            confirmed: true,
                            timestamp: data.timestamp
                        });
                        break;
                    
                    case 'user_joined':
                        setActiveUsers(prev => {
                            if (!prev.find(u => u.user_id === data.user_id)) {
                                return [...prev, { user_id: data.user_id, user_name: data.user_name }];
                            }
                            return prev;
                        });
                        if (onUserJoined) onUserJoined(data);
                        break;
                    
                    case 'user_left':
                        setActiveUsers(prev => prev.filter(u => u.user_id !== data.user_id));
                        if (onUserLeft) onUserLeft(data);
                        break;
                    
                    case 'user_typing':
                        setTypingUsers(prev => ({
                            ...prev,
                            [data.field]: {
                                user_id: data.user_id,
                                user_name: data.user_name,
                                timestamp: Date.now()
                            }
                        }));
                        // Auto-clear typing indicator after 3 seconds
                        setTimeout(() => {
                            setTypingUsers(prev => {
                                const newState = { ...prev };
                                if (newState[data.field]?.user_id === data.user_id) {
                                    delete newState[data.field];
                                }
                                return newState;
                            });
                        }, 3000);
                        break;

                    case 'new_comment':
                        if (onCommentAdded) {
                            onCommentAdded(data.comment);
                        }
                        break;

                    case 'comment_updated':
                        if (onCommentUpdated) {
                            onCommentUpdated(data.comment);
                        }
                        break;

                    case 'comment_deleted':
                        if (onCommentDeleted) {
                            onCommentDeleted(data.comment_id);
                        }
                        break;

                    case 'pong':
                        // Keep-alive response
                        break;
                    
                    default:
                        console.log('Unknown WebSocket message type:', data.type);
                }
            } catch (e) {
                console.error('Error parsing WebSocket message:', e);
            }
        };
        
        ws.onclose = (event) => {
            console.log('WebSocket closed:', event.code, event.reason);
            setIsConnected(false);
            
            // Clear ping interval
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
            }
            
            // Auto-reconnect if enabled and not a deliberate close
            if (autoReconnect && event.code !== 1000 && event.code !== 4001) {
                reconnectTimeoutRef.current = setTimeout(() => {
                    console.log('Attempting to reconnect...');
                    connect();
                }, 3000);
            }
        };
        
        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };
    }, [taskId, token, onFieldUpdate, onUserJoined, onUserLeft, autoReconnect]);

    // Connect when taskId changes
    useEffect(() => {
        connect();
        
        return () => {
            // Cleanup on unmount
            if (wsRef.current) {
                wsRef.current.close(1000, 'Component unmounted');
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
            }
        };
    }, [connect]);

    // Send update to server
    const sendUpdate = useCallback((field, value) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'update',
                field,
                value
            }));
        }
    }, []);

    // Send typing indicator
    const sendTyping = useCallback((field) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'typing',
                field
            }));
        }
    }, []);

    // Send comment via WebSocket
    const sendComment = useCallback((content, mentions = [], attachments = []) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'comment',
                content,
                mentions,
                attachments
            }));
            return true;
        }
        return false;
    }, []);

    // Update comment via WebSocket
    const sendCommentUpdate = useCallback((commentId, content, mentions = []) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'comment_update',
                comment_id: commentId,
                content,
                mentions
            }));
            return true;
        }
        return false;
    }, []);

    // Delete comment via WebSocket
    const sendCommentDelete = useCallback((commentId) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'comment_delete',
                comment_id: commentId
            }));
            return true;
        }
        return false;
    }, []);

    // Manual disconnect
    const disconnect = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close(1000, 'Manual disconnect');
        }
    }, []);

    return {
        isConnected,
        lastUpdate,
        activeUsers,
        typingUsers,
        sendUpdate,
        sendTyping,
        sendComment,
        sendCommentUpdate,
        sendCommentDelete,
        disconnect,
        reconnect: connect
    };
}

export default useTaskWebSocket;
